import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { encryptIdForUrl } from "@/utils/urlCrypto";
import {
  Building2,
  Search,
  Filter,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Eye,
  MoreVertical,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { AppConfig } from "@/datasources/AppConfig";
import { Logo } from "@/types/media";
import { resolveMediaUrl } from "@/lib/media";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useCurrentOuvidoriaStore } from "@/stores/currentOuvidoriaStore";

const api = new RemoteDataSource(Settings.urlBase);

interface OuvidoriaApiResponse {
  id: string;
  cnpj: string;
  nome: string;
  arquivada: boolean;
  geral: boolean;
  logo?: Logo | null;
  nomeFantasia?: string | null;
  etiquetas?: string[] | null;
  endereco?: {
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  } | null;
  status?: "Aguardando Aprovação" | "Aprovada" | "Reprovada" | null;
  instituicao?: "Prefeitura" | "Setorial" | "Associação de Moradores" | null;
}

interface OuvidoriaSetorial {
  id: string;
  nome: string;
  logoUrl?: string | null;
  secretaria: string | null;
  status: "Aguardando Aprovação" | "Aprovada" | "Reprovada" | null;
  instituicao: "Prefeitura" | "Setorial" | "Associação de Moradores" | null;
  created_at: string;
  total_solicitacoes?: number;
  solicitacoes_pendentes?: number;
  taxa_resolucao?: number;
  tempo_medio_resposta?: number;
}

const statusConfig = {
  "Aguardando Aprovação": {
    label: "Aguardando Aprovação",
    variant: "secondary" as const,
    className: "bg-warning/10 text-warning border-warning/20",
  },
  Aprovada: {
    label: "Aprovada",
    variant: "default" as const,
    className: "bg-success/10 text-success border-success/20",
  },
  Reprovada: {
    label: "Reprovada",
    variant: "destructive" as const,
    className: "bg-destructive/10 text-destructive border-destructive/20",
  },
};

const mapOuvidoriaToSetorial = (
  ouvidoria: OuvidoriaApiResponse,
): OuvidoriaSetorial => {
  return {
    id: ouvidoria.id,
    nome: ouvidoria.nomeFantasia || "Ouvidoria sem nome",
    logoUrl: resolveMediaUrl(ouvidoria.logo?.arquivo) || ouvidoria.logo?.arquivo,
    secretaria: ouvidoria.nome || null,
    status: ouvidoria.status || null,
    instituicao: ouvidoria.instituicao || null,
    created_at: new Date().toISOString(),
  };
};

export default function OuvidoriasSupervisionadasPage() {
  const navigate = useNavigate();

  const handleOuvidoriaClick = async (id: string) => {
    const encryptedId = await encryptIdForUrl(id);
    navigate(`/ouvidorias-supervisionadas/${encryptedId}`);
  };
  const { isReadOnly, isGestor } = useAgentePermissions();
  const ouvidoriaAtual = useCurrentOuvidoriaStore((s) => s.ouvidoria);
  const fetchCurrentOuvidoria = useCurrentOuvidoriaStore(
    (s) => s.fetchCurrentOuvidoria,
  );

  // Endpoint dinâmico - definido dentro do componente para usar AppConfig já inicializado
  const SUPERVISIONADAS_ENDPOINT = `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/supervisionadas`;

  // Garante que a ouvidoria atual esteja carregada para sabermos se é a Geral
  useEffect(() => {
    fetchCurrentOuvidoria();
  }, [fetchCurrentOuvidoria]);

  // Só o gestor de uma ouvidoria geral pode cadastrar novas ouvidorias setoriais
  const podeCriarSetorial =
    isGestor && ouvidoriaAtual?.geral === true && !isReadOnly;
  const [ouvidorias, setOuvidorias] = useState<OuvidoriaSetorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"todas" | "AguardandoAprovacao" | "Aprovada" | "Reprovada">("todas");
  const [instituicaoFilter, setInstituicaoFilter] = useState<"todas" | "Prefeitura" | "Setorial" | "AssociacaoDeMoradores">("todas");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const fetchOuvidoriasSupervisionadas = useCallback(
    async (page = 1, reset = false) => {
      if (reset) {
        setLoading(true);
        setPageNumber(1);
        setOuvidorias([]);
      } else if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const queryParams: Record<string, number | string> = {
          PageNumber: page,
          PageSize: pageSize,
        };

        if (instituicaoFilter !== "todas") {
          queryParams.instituicao = instituicaoFilter;
        }

        if (statusFilter !== "todas") {
          queryParams.status = statusFilter;
        }

        const result = await api.get<OuvidoriaApiResponse[]>({
          endpoint: SUPERVISIONADAS_ENDPOINT,
          headers: { accept: "application/vnd.data.compact.v1+json" },
          forceRefresh: true,
          queryParams,
          serialize: (data) => {
            if (Array.isArray(data)) return data;
            return [];
          },
        });

        if ("failure" in result) {
          if (reset || page === 1) {
            setOuvidorias([]);
          }
          return;
        }

        const ouvidoriasApi = result.success.data || [];
        const ouvidoriasComDados = ouvidoriasApi.map(mapOuvidoriaToSetorial);
        const hasMoreData = ouvidoriasApi.length === pageSize;

        if (reset || page === 1) {
          setOuvidorias(ouvidoriasComDados);
          setPageNumber(page);
          setHasMore(hasMoreData);
        } else {
          setOuvidorias((prev) => [...prev, ...ouvidoriasComDados]);
          setPageNumber(page);
          setHasMore(hasMoreData);
        }
      } catch (error) {
        if (reset || page === 1) {
          setOuvidorias([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [SUPERVISIONADAS_ENDPOINT, pageSize, statusFilter, instituicaoFilter],
  );

  useEffect(() => {
    fetchOuvidoriasSupervisionadas(1, true);
  }, [fetchOuvidoriasSupervisionadas]);

  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && !loading) {
      fetchOuvidoriasSupervisionadas(pageNumber + 1, false);
    }
  }, [
    hasMore,
    loadingMore,
    loading,
    pageNumber,
    fetchOuvidoriasSupervisionadas,
  ]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );
    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [loadMore, hasMore, loadingMore, loading]);

  // Reset pagination on filter changes
  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    setOuvidorias([]);
    fetchOuvidoriasSupervisionadas(1, true);
  }, [searchTerm, statusFilter, instituicaoFilter, fetchOuvidoriasSupervisionadas]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega a lista
  useEffect(() => {
    const handleFocus = () => {
      setPageNumber(1);
      setHasMore(true);
      setOuvidorias([]);
      fetchOuvidoriasSupervisionadas(1, true);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setPageNumber(1);
        setHasMore(true);
        setOuvidorias([]);
        fetchOuvidoriasSupervisionadas(1, true);
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [searchTerm, statusFilter, fetchOuvidoriasSupervisionadas]);

  const filteredOuvidorias = ouvidorias.filter((ouv) => {
    const matchesSearch =
      ouv.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ouv.secretaria?.toLowerCase().includes(searchTerm.toLowerCase());
    // Mapeia o valor da API para o valor do filtro
    const statusMap: Record<string, string> = {
      "Aguardando Aprovação": "AguardandoAprovacao",
      "Aprovada": "Aprovada",
      "Reprovada": "Reprovada",
    };
    const normalizedStatus = statusMap[ouv.status || ""] || ouv.status;
    const matchesStatus =
      statusFilter === "todas" || normalizedStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleChangeStatus = useCallback(async (ouvidoriaId: string, newStatus: "Aprovada" | "Reprovada") => {
    try {
      const endpoint = `v1/municipios/${AppConfig.municipioId}/ouvidorias/${ouvidoriaId}/alterar-status`;
      const result = await api.post({
        endpoint,
        queryParams: {
          status: newStatus,
        },
        headers: { 
          Accept: "application/force+json" 
        },
      });

      if ("failure" in result) {
        console.error("Erro ao alterar status:", result.failure);
        return;
      }

      // Refresh the list after successful status change
      fetchOuvidoriasSupervisionadas(1, true);
    } catch (error) {
      console.error("Erro ao alterar status:", error);
    }
  }, [fetchOuvidoriasSupervisionadas]);

  const stats = {
    total: ouvidorias.length,
    ativas: ouvidorias.filter((o) => o.status === "Aprovada").length,
    pendentes: ouvidorias.filter((o) => o.status === "Aguardando Aprovação").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="heading-page">Ouvidorias Supervisionadas</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie e monitore as ouvidorias setoriais do seu município
          </p>
        </div>
        {podeCriarSetorial && (
          <Button
            className="gap-2"
            onClick={() =>
              navigate("/criar-ouvidoria", { state: { modoCadastro: "admin" } })
            }
          >
            <Building2 className="h-4 w-4" />
            Nova Ouvidoria Setorial
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total de Ouvidorias
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Ouvidorias setoriais cadastradas
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ouvidorias Ativas
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {stats.ativas}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Em pleno funcionamento
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pendentes de Aprovação
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">
                {stats.pendentes}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Aguardando ativação
              </p>
            </CardContent>
          </Card>
        </motion.div>

      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">Lista de Ouvidorias</CardTitle>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar ouvidoria..."
                  className="pl-9 w-full sm:w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as "todas" | "AguardandoAprovacao" | "Aprovada" | "Reprovada")}>
                <SelectTrigger className="w-full sm:w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="AguardandoAprovacao">Aguardando Aprovação</SelectItem>
                  <SelectItem value="Aprovada">Aprovada</SelectItem>
                  <SelectItem value="Reprovada">Reprovada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={instituicaoFilter} onValueChange={(value) => setInstituicaoFilter(value as "todas" | "Prefeitura" | "Setorial" | "AssociacaoDeMoradores")}>
                <SelectTrigger className="w-full sm:w-40">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Instituição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="Prefeitura">Prefeitura</SelectItem>
                  <SelectItem value="Setorial">Setorial</SelectItem>
                  <SelectItem value="Associacao">Associação de Moradores</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          ) : filteredOuvidorias.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-medium">
                Nenhuma ouvidoria encontrada
              </h3>
              <p className="text-muted-foreground mt-1">
                {searchTerm || statusFilter !== "todas"
                  ? "Tente ajustar os filtros de busca"
                  : "Cadastre a primeira ouvidoria setorial"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ouvidoria</TableHead>
                    <TableHead>Secretaria</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOuvidorias.map((ouvidoria, index) => (
                    <motion.tr
                      key={ouvidoria.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group cursor-pointer hover:bg-muted/50"
                      onClick={() => handleOuvidoriaClick(ouvidoria.id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {ouvidoria.logoUrl ? (
                            <img
                              src={ouvidoria.logoUrl}
                              alt={ouvidoria.nome}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{ouvidoria.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              Criada em{" "}
                              {new Date(
                                ouvidoria.created_at,
                              ).toLocaleDateString("pt-BR")}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {ouvidoria.secretaria || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {ouvidoria.instituicao || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {ouvidoria.status ? (
                          <Badge
                            variant={statusConfig[ouvidoria.status].variant}
                            className={statusConfig[ouvidoria.status].className}
                          >
                            {statusConfig[ouvidoria.status].label}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-muted text-muted-foreground">
                            Sem status
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleOuvidoriaClick(ouvidoria.id);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver detalhes
                            </DropdownMenuItem>
                            {ouvidoria.status && (
                              <>
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(ouvidoria.id, "Aprovada");
                                  }}
                                  disabled={ouvidoria.status === "Aprovada"}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Aprovar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(ouvidoria.id, "Reprovada");
                                  }}
                                  disabled={ouvidoria.status === "Reprovada"}
                                >
                                  <AlertCircle className="h-4 w-4 mr-2" />
                                  Reprovar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
              {loadingMore && (
                <div className="flex items-center justify-center py-4">
                  <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <div ref={observerTarget} className="h-1" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
